/**
 * CADAIAnalysisPanel - AI analysis results display
 *
 * Renders Markdown-formatted analysis as styled pre-formatted text.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, Sparkles, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useConfirm } from '@hooks/useConfirm';
import { LoadingButton } from '../common/LoadingButton';
import { analyzeCADModel } from '../../services/cad.service';
import { resolveApiErrorMessage } from '../../utils/apiErrorMessages';
import { renderAnalysisMarkdown } from './ai-analysis/renderAnalysisMarkdown';

interface CADAIAnalysisPanelProps {
  modelId: string | null;
  existingAnalysis: string | null;
  onAnalysisComplete?: (analysis: string) => void;
}

const CADAIAnalysisPanel: React.FC<CADAIAnalysisPanelProps> = ({
  modelId,
  existingAnalysis,
  onAnalysisComplete,
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [analysis, setAnalysis] = useState<string | null>(existingAnalysis);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyze = async () => {
    if (!modelId) return;
    if (
      analysis &&
      !(await confirm({
        title: t('cad.ai.reanalyzeConfirm.title'),
        description: t('cad.ai.reanalyzeConfirm.description'),
        confirmLabel: t('cad.ai.reanalyzeConfirm.confirm'),
      }))
    ) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await analyzeCADModel(modelId);
      setAnalysis(result.analysis);
      onAnalysisComplete?.(result.analysis);
    } catch (err) {
      setError(resolveApiErrorMessage(t, err, { fallbackKey: 'cad.ai.failed' }));
    } finally {
      setLoading(false);
    }
  };

  // Update when existingAnalysis changes (model selection)
  React.useEffect(() => {
    setAnalysis(existingAnalysis);
    setError(null);
  }, [existingAnalysis, modelId]);

  if (!modelId) {
    return (
      <div className="p-4 text-center">
        <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
          {t('cad.ai.selectModel')}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        {/* min-w-0 so the title wraps instead of widening the fixed-width column. */}
        <h3 className="revy-cjk min-w-0 text-sm font-semibold leading-[1.5]">
          {t('cad.ai.title')}
        </h3>
        <LoadingButton
          type="button"
          size="sm"
          variant={analysis ? 'outline' : 'default'}
          loading={loading}
          loadingText={t('cad.ai.analyzing')}
          onClick={handleAnalyze}
        >
          <Sparkles strokeWidth={1.9} />
          <span className="revy-cjk">
            {analysis ? t('cad.ai.reanalyze') : t('cad.ai.start')}
          </span>
        </LoadingButton>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-2 pr-10">
          {/* Ahead of the icon so alert.tsx's `[&>svg~*]:pl-7` rule skips it. */}
          <button
            type="button"
            onClick={() => setError(null)}
            className="absolute right-2 top-2 grid size-7 place-items-center rounded-sm hover:bg-destructive/10"
          >
            <X strokeWidth={2} className="size-4" />
            <span className="sr-only">{t('common.close')}</span>
          </button>
          <CircleAlert strokeWidth={1.9} className="size-4" />
          <AlertDescription className="revy-cjk leading-[1.5]">{error}</AlertDescription>
        </Alert>
      )}

      {analysis && (
        <div className="max-h-[400px] overflow-auto rounded-lg border p-3">
          {renderAnalysisMarkdown(analysis)}
        </div>
      )}

      {!analysis && !loading && (
        <p className="revy-cjk text-xs leading-[1.5] text-muted-foreground">
          {t('cad.ai.description')}
        </p>
      )}
    </div>
  );
};

export default CADAIAnalysisPanel;
